(() => {
  const INPUT_DELAY = 500;

  const debounceLocal = (callback, wait) => {
    let timeoutId;

    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback(...args), wait);
    };
  };

  const isSearchPage = () =>
    document.body.classList.contains('template-search') || /\/search\/?$/.test(window.location.pathname);

  const getSearchTerm = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchInput = document.querySelector('#Search-In-Template');
    const facetSearchInput = document.querySelector(
      '#FacetFiltersForm input[name="q"], #FacetFiltersFormMobile input[name="q"]'
    );

    return String(urlParams.get('q') || searchInput?.value || facetSearchInput?.value || '').trim();
  };

  const getFormsForRequest = (triggerForm) => {
    if (triggerForm?.id === 'FacetFiltersFormMobile') {
      return [triggerForm];
    }

    return ['FacetFiltersForm', 'FacetSortForm', 'FacetSortDrawerForm', 'FacetFiltersPillsForm']
      .map((id) => document.getElementById(id))
      .filter(Boolean);
  };

  const resetDynamicParams = (params) => {
    [...params.keys()].forEach((key) => {
      if (key === 'page' || key === 'section_id' || key === 'sort_by' || key.startsWith('filter.')) {
        params.delete(key);
      }
    });
  };

  const addFormToParams = (params, form) => {
    const formData = new FormData(form);

    for (const [name, rawValue] of formData.entries()) {
      const value = String(rawValue).trim();
      if (!value || name === 'page' || name === 'section_id') continue;

      if (name === 'sort_by' || name === 'q' || name === 'options[prefix]' || name === 'type') {
        params.set(name, value);
      } else {
        params.append(name, value);
      }
    }
  };

  const buildSearchParams = (triggerForm) => {
    const params = new URLSearchParams(window.location.search);
    resetDynamicParams(params);

    getFormsForRequest(triggerForm).forEach((form) => addFormToParams(params, form));

    if (isSearchPage()) {
      const searchTerm = getSearchTerm();

      if (searchTerm) {
        params.set('q', searchTerm);
      } else {
        params.delete('q');
      }

      params.set('options[prefix]', 'last');
      params.set('type', 'product');
    }

    params.delete('page');
    params.delete('section_id');

    return params;
  };

  const navigateWithParams = (params) => {
    const queryString = params.toString();
    const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (nextUrl !== currentUrl) {
      window.location.assign(nextUrl);
    }
  };

  class FacetFiltersForm extends HTMLElement {
    constructor() {
      super();

      const form = this.querySelector('form');
      if (!form) return;

      const submitFilters = debounceLocal(() => {
        navigateWithParams(buildSearchParams(form));
      }, INPUT_DELAY);

      form.addEventListener('input', submitFilters);
    }

    onActiveFilterClick(event) {
      event.preventDefault();

      const link = event.currentTarget;
      const url = new URL(link.href, window.location.origin);

      url.searchParams.delete('page');
      url.searchParams.delete('section_id');

      if (isSearchPage()) {
        const searchTerm = getSearchTerm();

        if (searchTerm) {
          url.searchParams.set('q', searchTerm);
        }

        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('type', 'product');
      }

      navigateWithParams(url.searchParams);
    }
  }

  if (!customElements.get('facet-filters-form')) {
    customElements.define('facet-filters-form', FacetFiltersForm);
  }

  class PriceRange extends HTMLElement {
    constructor() {
      super();

      this.querySelectorAll('input').forEach((input) => {
        input.addEventListener('change', this.onRangeChange.bind(this));
        input.addEventListener('keydown', this.onKeyDown.bind(this));
      });

      this.setMinAndMaxValues();
    }

    onRangeChange(event) {
      this.adjustToValidValues(event.currentTarget);
      this.setMinAndMaxValues();
    }

    onKeyDown(event) {
      if (event.metaKey || event.ctrlKey) return;

      const allowedKeys = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
      if (!event.key.match(allowedKeys)) event.preventDefault();
    }

    setMinAndMaxValues() {
      const inputs = this.querySelectorAll('input');
      const minInput = inputs[0];
      const maxInput = inputs[1];

      if (!minInput || !maxInput) return;

      if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
      if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
      if (minInput.value === '') maxInput.setAttribute('data-min', 0);
      if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
    }

    adjustToValidValues(input) {
      const value = Number(input.value);
      const min = Number(input.getAttribute('data-min'));
      const max = Number(input.getAttribute('data-max'));

      if (Number.isNaN(value)) return;
      if (value < min) input.value = min;
      if (value > max) input.value = max;
    }
  }

  if (!customElements.get('price-range')) {
    customElements.define('price-range', PriceRange);
  }

  class FacetRemove extends HTMLElement {
    constructor() {
      super();

      const facetLink = this.querySelector('a');
      if (!facetLink) return;

      facetLink.setAttribute('role', 'button');
      facetLink.addEventListener('click', this.closeFilter.bind(this));
      facetLink.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() !== 'SPACE') return;
        event.preventDefault();
        this.closeFilter(event);
      });
    }

    closeFilter(event) {
      event.preventDefault();

      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

      if (form && typeof form.onActiveFilterClick === 'function') {
        form.onActiveFilterClick(event);
        return;
      }

      window.location.assign(event.currentTarget.href);
    }
  }

  if (!customElements.get('facet-remove')) {
    customElements.define('facet-remove', FacetRemove);
  }
})();